import { GeoJSON } from 'geojson';

import { Feature } from 'ol';
import { Geometry } from 'ol/geom';

import { DATA_PROJECTION, GEOSERVER_URL } from 'config';
import { LayerManager } from 'models';

import { makeAPICall } from 'models/api';
import { GeoJSON as olGeoJson } from 'ol/format';
import { LegendModel } from './legend';
import { IdentifyFeaturesParams } from './identify';
// import { toLonLat } from 'ol/proj';
/**
 * Meant to define the relevant details of a Web Map Service (WMS)
 * To avoid rewriting the wheel too much, best to implement from OpenLayers (import 'ol') types or
 * have types that extend them for our own specific use cases.
 *
 * For a general WMS Spec see this url:
 * https://docs.geoserver.org/stable/en/user/services/wms/reference.html
 *
 */
export declare namespace WMS {
    export type GetCapabilitiesQuery = Pick<
        WMS.Query,
        | 'request'
        | 'service'
        | 'version'
        | 'request'
        | 'namespace'
        | 'format'
        | 'rootLayer'
    >;

    export type GetLegendGraphic = Pick<
        WMS.Query,
        | 'service'
        | 'version'
        | 'request'
        | 'layer'
        | 'format'
        | 'legend_options'
        | 'width'
        | 'height'
        | 'style'
    >;

    export type GetMapQuery = Pick<
        WMS.Query,
        | 'request'
        | 'service'
        | 'version'
        | 'transparent'
        | 'bgcolor'
        | 'exceptions'
        | 'time'
        | 'sld'
        | 'sld_body'
    > &
        Required<
            Pick<
                WMS.Query,
                | 'layers'
                | 'styles'
                | 'srs'
                | 'bbox'
                | 'width'
                | 'height'
                | 'format'
            >
        >;

    export type GetFeatureInfoQuery = Pick<
        WMS.Query,
        | 'request'
        | 'service'
        | 'version'
        | 'info_format'
        | 'feature_count'
        | 'exceptions'
        | 'buffer'
        | 'cql_filter'
        | 'filter'
        | 'propertyName'
        | 'exclude_nodata_result'
        | 'i'
        | 'j'
    > &
        Required<
            Pick<
                WMS.Query,
                | 'layers'
                | 'styles'
                | 'srs'
                | 'bbox'
                | 'width'
                | 'height'
                | 'query_layers'
                | 'x'
                | 'y'
            >
        >;

    /**
     * Openlayers doesn't exactly have a query object/interface that can easily be interchanged.
     * Generally the url is built from wmslayer.getFeatureInfoURL however this takes info from both the the
     * layer object and parameters to that method. Instead we want a query interface that is detached from any layer object.
     * We want this so it's more standard when passing it around, and more standard functions can then be created.
     */
    export interface Query {
        /* Shared between most request Types */
        service: 'WMS';
        version: '1.0.0' | '1.1.0' | '1.1.1' | '1.3.0';
        request:
            | 'GetCapabilities'
            | 'GetMap'
            | 'GetFeatureInfo'
            | 'DescribeLayer'
            | 'GetLegendGraphic';
        // As per https://docs.geoserver.org/stable/en/user/services/wms/reference.html#exceptions default xml
        exceptions?:
            | 'application/vnd.ogc.se_xml'
            | 'application/vnd.ogc.se_inimage'
            | 'application/vnd.ogc.se_blank'
            | 'application/vnd.gs.wms_partial'
            | 'application/json'
            | 'text/javascript';

        // As per https://docs.geoserver.org/stable/en/user/services/wms/outputformats.html#wms-output-formats
        format?:
            | 'image/png'
            | 'image/png8'
            | 'image/jpeg'
            | 'image/vnd.jpeg-png'
            | 'image/vnd.jpeg-png8'
            | 'image/gif'
            | 'image/tiff8'
            | 'image/geotiff'
            | 'image/geotiff8'
            | 'image/svg'
            | 'application/pdf'
            | 'rss'
            | 'kml'
            | 'kmz'
            | 'application/openlayers'
            | 'application/json;type=utfgrid'
            | 'application/json';

        /* Specific to both GetFeatureInfo and GetMap */
        // defined to be a comma separated list
        layers?: string;
        // defined to be a comma separated list
        styles?: string;
        srs?: string;
        // at least one of src or crs must exist as per defintion, here if crs exists, it will override src which may then be empty
        crs?: string;
        // by definition normally formatted as minx,miny,maxx,maxy
        bbox?: string;
        // in pixels
        width?: number;
        // in pixels
        height?: number;

        /* GetFeatureInfo */
        // normally a comma separated list
        query_layers?: string[];
        info_format?:
            | 'text/plain'
            | 'application/vnd.ogc.gml'
            | 'application/vnd.ogc.gml/3.1.1'
            | 'text/html'
            | 'application/json'
            | 'text/javascript';
        // max features to return: defaults to 1
        feature_count?: number;
        // X and Y coords of point on map
        x?: number;
        y?: number;
        // I and J are parameter keys for the X/Y points on the map (at least x/y or i/j must exist)
        i?: string;
        j?: string;

        // Some vendor specific parameters see: https://docs.geoserver.org/stable/en/user/services/wms/vendor.html#wms-vendor-parameters
        buffer?: number;
        // ECQL format, see: https://docs.geoserver.org/latest/en/user/filter/ecql_reference.html
        cql_filter?: string;
        // OGC format, see https://docs.geoserver.org/latest/en/user/filter/filter_reference.html
        filter?: string;
        // feature properties to be returned
        propertyName?: string[];
        exclude_nodata_result?: boolean;

        /* Specific to GetMap */

        // default false
        transparent?: boolean;
        // format RRGGBB (default white 'FFFFFF')
        bgcolor?: string;
        // general format yyyy-MM-ddThh:mm:ss.SSSZ see: https://docs.geoserver.org/stable/en/user/services/wms/time.html#wms-time
        time?: string;
        // references a styled layer descriptor XML file, see: https://docs.geoserver.org/stable/en/user/styling/index.html#styling
        sld?: string;
        // url encoded body of file that can be referenced above
        sld_body?: string;

        /* Special to get Capabilities */
        namespace?: string;
        rootLayer?: boolean;

        // the name of the layer
        layer?: string;

        // named style to apply (GetLegendGraphic uses singular 'style', not 'styles')
        style?: string;

        legend_options?: string;
    }

    export namespace WMSGetFeatureInfoResponse {
        export type Properties = Record<string, any>;

        export interface Feature {
            type: string;
            id: string;
            geometry?: any;
            properties: Properties;
        }

        export interface Root {
            type: string;
            features: Feature[];
            totalFeatures: string;
            numberReturned: number;
            timeStamp: string;
            crs?: any;
        }
    }
}

const makeGetLegendQueryDefaults: Pick<
    WMS.GetLegendGraphic,
    'request' | 'service' | 'version' | 'format' | 'layer'
> = {
    request: 'GetLegendGraphic',
    service: 'WMS',
    version: '1.1.1',
    format: 'application/json',
};

export function makeGetLegendQuery(
    params?: Omit<
        WMS.GetLegendGraphic,
        keyof typeof makeGetLegendQueryDefaults
    > &
        Partial<typeof makeGetLegendQueryDefaults>,
): WMS.GetLegendGraphic {
    return {
        ...makeGetLegendQueryDefaults,
        ...params,
    };
}

const convertParameters = (request: any, skipEncoding?: boolean) =>
    Object.keys(request)
        .map(
            (key) =>
                `${key}=${
                    skipEncoding
                        ? `${(request as any)[key]}`
                        : encodeURIComponent((request as any)[key])
                }`,
        )
        .join('&');

export async function callWMS<T>(request: WMS.Query) {
    const parameters = convertParameters(request);
    const wmsPath = `${GEOSERVER_URL}/wms`;

    const response = await fetch(`${wmsPath}?${parameters}`);
    return (await response.json()) as T;
}

// export async function queryWMS(
//     layers: LayerManager.LayerConfig[],
//     coords: number[],
//     auth?: AuthManager.OAuth2Creds | null,
//     date?: Date,
//     issueTime?: Date,
//     format = 'application/json',
// ): Promise<WMS.WMSGetFeatureInfoResponse.Root | string | null> {
//     const layerIdString = layers.map((l) => l.serviceName).join(',');
//     if (!layerIdString) return null;

//     const preprocessedParams: Record<string, any> = {
//         service: 'WMS',
//         version: '1.1.0',
//         request: 'GetFeatureInfo',
//         layers: layerIdString,
//         query_layers: layerIdString,
//         bbox: [
//             ...coords.map((x) => x - 0.01),
//             ...coords.map((x) => x + 0.01),
//         ].join(','),
//         srs: 'EPSG:4326',
//         width: 10,
//         height: 10,
//         x: 5,
//         y: 5,
//         info_format: format,
//         feature_count: 50,
//     };

//     if (date) preprocessedParams.TIME = date.toISOString();
//     if (issueTime) preprocessedParams.DIM_ISSUE_TIME = issueTime.toISOString();

//     const parameters = convertParameters(preprocessedParams);

//     const wmsPath = `${GEOSERVER_URL}/wms`;

//     const requestHeaders: HeadersInit = new Headers();
//     if (auth?.accessToken && auth.accessToken.length > 0) {
//         requestHeaders.set(
//             'Authorization',
//             `${auth.tokenType} ${auth.accessToken}`,
//         );
//     }

//     try {
//         const response = await fetch(`${wmsPath}?${parameters}`, {
//             headers: requestHeaders,
//         });

//         if (format == 'application/json') {
//             const json =
//                 (await response.json()) as WMS.WMSGetFeatureInfoResponse.Root;
//             return json;
//         } else {
//             return await response.text();
//         }
//     } catch (e) {
//         return null;
//     }
// }

// export async function queryWFS<Output = GeoJSON>(
//     layers: LayerManager.LayerConfig[],
//     propertyName?: string | null,
//     auth?: AuthManager.OAuth2Creds | null,
// ): Promise<Output | null> {
//     const layerIdString = layers.map((l) => l.serviceName).join(',');
//     if (!layerIdString) return null;

//     const preprocessedParams: Record<string, any> = {
//         service: 'WFS',
//         version: '1.0.0',
//         request: 'GetFeature',
//         layers: layerIdString,
//         outputFormat: 'application/json',
//     };

//     if (propertyName) preprocessedParams.propertyName = propertyName ?? '';

//     const parameters = convertParameters(preprocessedParams);

//     const path = `${GEOSERVER_URL}/ows`;

//     const requestHeaders: HeadersInit = new Headers();
//     if (auth?.accessToken && auth.accessToken.length > 0) {
//         requestHeaders.set(
//             'Authorization',
//             `${auth.tokenType} ${auth.accessToken}`,
//         );
//     }

//     try {
//         const response = await fetch(`${path}?${parameters}`, {
//             headers: requestHeaders,
//         });
//         const json = (await response.json()) as Output;
//         return json;
//     } catch (e) {
//         return null;
//     }
// }

/**
 * @param selectedLayerId id of the layers selected by the user.
 * @param grayIndex value provided by the raster pixel.
 * @returns the entry pertaining to the grayIndex provided.
 */
export async function queryRasterLayerlegend(
    selectedLayerId: LayerManager.Layer.Id,
    grayIndex: number,
): Promise<Record<string, any>> {
    const query = makeGetLegendQuery({
        layer: selectedLayerId,
    });

    const response = await callWMS<LegendModel.Root>(query);

    const entries =
        response.Legend[0].rules[0].symbolizers[0].Raster?.colormap?.entries;

    const noEntriesFound = {
        [selectedLayerId]: 'No legend entries for this raster layer',
    };

    if (!entries) {
        return noEntriesFound;
    }

    const correctEntry = entries.find(
        (entry: any) => +entry.quantity >= grayIndex,
    );

    if (!correctEntry) {
        return noEntriesFound;
    }

    return correctEntry;
}

/**
 * Returns an array of property objects pertaining to the
 * wsm layer the user has selected to display on the map.
 */
// export async function queryWMSFeaturesOld(
//     layers: LayerManager.LayerConfig[],
//     coords: number[],
//     auth?: AuthManager.OAuth2Creds | null,
//     date?: Date,
//     issueTime?: Date,
// ): Promise<Feature<Geometry>[]> {
//     try {
//         const response = await queryWMS(
//             layers,
//             coords,
//             auth,
//             date,
//             issueTime,
//             'text/xml',
//         );

//         if (!response) {
//             return [];
//         }

//         const features = new WMSGetFeatureInfo().readFeatures(
//             response,
//         ) as Feature<Geometry>[];

//         return features;
//     } catch (e) {
//         return [];
//     }
// }

// interface QueryWMSFeaturesParams {
//     layers: AbstractLayer[];
//     coords: number[];
//     srs?: string;
//     date?: Date;
//     issueTime?: Date;
//     filter?: string;
//     map?: OLMap;
// }

/**
 * Returns an array of property objects pertaining to the
 * wsm layer the user has selected to display on the map.
 *
 */
// export const queryWMSFeatures = makeAPICall<
//     Feature<Geometry>[],
//     QueryWMSFeaturesParams,
//     string
// >(
//     ({ coords, layers, srs, date, issueTime, filter }) => {
//         const layerIdString = (
//             layers
//                 .map((l) => l.config.serviceName)
//                 .filter((x) => !!x) as string[]
//         ).join(',');

//         const params = new URLSearchParams({
//             service: 'WMS',
//             version: '1.1.0',
//             request: 'GetFeatureInfo',
//             layers: layerIdString,
//             query_layers: layerIdString,
//             bbox: [
//                 ...coords.map((x) => x - 0.01),
//                 ...coords.map((x) => x + 0.01),
//             ].join(','),
//             srs: srs ?? 'EPSG:4326',
//             width: '10',
//             height: '10',
//             x: '5',
//             y: '5',
//             //info_format: 'text/xml',
//             info_format: 'application/json',
//             feature_count: '50',
//             buffer: '1',
//         });

//         if (date) params.append('TIME', date.toISOString());
//         if (issueTime) params.append('DIM_ISSUE_TIME', issueTime.toISOString());
//         if (filter) params.append('cql_filter', filter);

//         return {
//             url: `${GEOSERVER_URL}/wms?${params.toString()}`,
//             dataType: 'text',
//         };
//     },
//     (root) => new olGeoJson().readFeatures(root) as Feature<Geometry>[],
//     //(root) => new WMSGetFeatureInfo().readFeatures(root) as Feature<Geometry>[],
// );

// const getFeatureInfoUrl = (
//     map: OLMap,
//     coords: number[],
//     layers: AbstractLayer[],
//     date?: Date,
//     issueTime?: Date,
//     filter?: string,
// ) => {
//     const layerIdString = (
//         layers.map((l) => l.config.serviceName).filter((x) => !!x) as string[]
//     ).join(',');

//     const lonLat = toLonLat(coords);

//     const params = new URLSearchParams({
//         service: 'WMS',
//         version: '1.1.0',
//         request: 'GetFeatureInfo',
//         layers: layerIdString,
//         query_layers: layerIdString,
//         bbox: [
//             ...lonLat.map((x) => x - 0.01),
//             ...lonLat.map((x) => x + 0.01),
//         ].join(','),
//         srs: 'EPSG:4326',
//         width: '10',
//         height: '10',
//         x: '5',
//         y: '5',
//         info_format: 'application/json',
//     });

//     if (date) params.append('TIME', date.toISOString());
//     if (issueTime) params.append('DIM_ISSUE_TIME', issueTime.toISOString());
//     if (filter) params.append('cql_filter', filter);
//     return `${GEOSERVER_URL}/wms?${params.toString()}`;
// };

export const queryWMSFeaturesV2 = makeAPICall<
    Feature<Geometry>[],
    IdentifyFeaturesParams,
    string
>(
    ({ coords, layers, date, issueTime, filter, map }) => {
        const layerSource: any = layers
            .map((l) => l.getLayer()?.getSource())
            .filter((x) => !!x)
            ?.at(0);
        // if layersource or map object not available fallback to old method
        if (!layerSource || !map) {
            throw new Error('Invalid layerSource and Map Object');
        }

        const params = {
            INFO_FORMAT: 'application/json',
            BUFFER: '5',
            FEATURE_COUNT: '20',
            ...(date && { TIME: date.toISOString() }),
            ...(issueTime && { DIM_ISSUE_TIME: issueTime.toISOString() }),
            ...(filter && { CQL_FILTER: filter }),
        };
        if (!layerSource?.getFeatureInfoUrl) {
            throw new Error('Unable to get the featureInfo Url.');
        }
        const url = layerSource?.getFeatureInfoUrl(
            coords,
            map?.getView().getResolution(),
            map?.getView().getProjection(),
            params,
        );

        return {
            url: `${url}`,
            dataType: 'text',
        };
    },
    (root) => new olGeoJson().readFeatures(root) as Feature<Geometry>[],
);

// const timeBetweenCache = 60 * 60 * 1000; // Every hour
// const legendCache: Partial<
//     Record<LayerManager.Layer.Id, LegendModel.Legend | null | undefined>
// > = {};
// const legendCachePromise: Partial<
//     Record<
//         LayerManager.Layer.Id,
//         Promise<LegendModel.Legend> | null | undefined
//     >
// > = {};
// const legendLastUpdateTime: Partial<
//     Record<LayerManager.Layer.Id, number | undefined>
// > = {};

export const forceGetLegend = makeAPICall<
    LegendModel.Legend,
    { config: LayerManager.LayerConfig },
    LegendModel.Root
>(
    ({ config }) => {
        const query = makeGetLegendQuery({
            layer: config.serviceName ?? '',
            ...(config.wmsStyles && { style: config.wmsStyles }),
        });
        return {
            url: `${GEOSERVER_URL}/wms?${convertParameters(query)}`,
            skipAuth: false,
        };
    },
    (root) => root.Legend[0],
);

// export const getLegend = makeMockAPICall<
//     LegendModel.Legend | null,
//     { config: LayerManager.LayerConfig },
//     null
// >(
//     () => {},
//     async (_, payload, state) => {
//         const { config } = payload;

//         if (legendLastUpdateTime[config.id] == null)
//             legendLastUpdateTime[config.id] = 0;

//         if (
//             Date.now() - (legendLastUpdateTime[config.id] ?? 0) >
//                 timeBetweenCache &&
//             state?.auth.tokens.status === 'finished'
//         ) {
//             if (config.legend) {
//                 try {
//                     legendCache[config.id] = config.legend;
//                 } catch (err) {
//                     legendCache[config.id] = undefined;
//                 }
//             } else if (legendCachePromise[config.id]) {
//                 try {
//                     legendCache[config.id] = await legendCachePromise[
//                         config.id
//                     ];
//                 } catch (err) {
//                     legendCache[config.id] = undefined;
//                 }
//             } else {
//                 legendCachePromise[config.id] = forceGetLegend(payload);
//                 try {
//                     legendCache[config.id] = await legendCachePromise[
//                         config.id
//                     ];
//                 } catch (err) {
//                     legendCache[config.id] = undefined;
//                 }

//                 legendCachePromise[config.id] = null;
//                 legendLastUpdateTime[config.id] = Date.now();
//             }
//         }
//         return legendCache[config.id] ?? null;
//     },
//     null,
//     0,
// );

// export const getLegendImage = makeAPICall<
//     string,
//     { config: LayerManager.LayerConfig },
//     Blob
// >(
//     ({ config }) => {
//         const query = makeGetLegendQuery({
//             layer: config.serviceName ?? '',
//             format: 'image/png',
//             legend_options:
//                 'bgColor:0xFFFFFF%3BfontSize:12%3BfontName:Arial%3BfontStyle:bold%3Bdpi:96%3Bmx:0.01%3Bmy:0.01%3Bdx:8%3BforceLabels:on%3B',
//             width: 20,
//             height: 20,
//         });
//         return {
//             url: `${GEOSERVER_URL}/wms?${convertParameters(query)}`,
//             endpoint: GEOSERVER_URL,
//             skipAuth: false,
//             dataType: 'blob',
//         };
//     },
//     (imgBlob) => URL.createObjectURL(imgBlob),
// );

// export const queryWMSImage = makeAPICall<
//     string,
//     { layer?: LayerManager.LayerConfig; options: Partial<WMS.GetMapQuery> },
//     Blob
// >(
//     ({ layer, options }) => {
//         const query = {
//             layers: layer?.serviceName ?? '',
//             format: 'image/png',
//             transparent: true,
//             version: '1.1.0',
//             service: 'WMS',
//             request: 'GetMap',
//             srs: 'EPSG:4326',
//             ...options,
//         };
//         return {
//             url: `${GEOSERVER_URL}/wms?${convertParameters(query, true)}`,
//             endpoint: GEOSERVER_URL,
//             skipAuth: false,
//             dataType: 'blob',
//         };
//     },
//     (imgBlob) => URL.createObjectURL(imgBlob),
// );

export const getLegendEntry = (
    value: number | string,
    legend?: LegendModel.Legend | null,
    forceType?: string,
): LegendModel.GeoServerRasterColorMapEntry | null => {
    if (legend == null) return null;

    const legendType =
        forceType ||
        legend.rules[0].symbolizers[0]?.Raster?.colormap?.type ||
        (legend.rules[0].symbolizers[0]?.Point?.url && 'graphic');

    const entries = legend.rules[0].symbolizers[0]?.Raster?.colormap?.entries;
    let entry = null;
    if (entries) {
        if (legendType === 'intervals' || legendType === 'ramp') {
            entry = entries.find((item) => +item.quantity > +`${value}`);
        } else if (legendType === 'values') {
            entry = entries.find((item) => item.quantity === `${value}`);
        }
    }
    return entry ?? null;
};

export const getColour = (
    value: number | string,
    legend?: LegendModel.Legend | null,
    forceType?: string,
): string | null => {
    return getLegendEntry(value, legend, forceType)?.color ?? null;
};

export const getLabel = (
    value: number | string,
    legend: LegendModel.Legend,
    forceType?: string,
): string | null => {
    return getLegendEntry(value, legend, forceType)?.label ?? null;
};

export const getBFMCNames = makeAPICall<string[], void, GeoJSON>(
    () => ({
        url: `${GEOSERVER_URL}/ows?${new URLSearchParams({
            service: 'WFS',
            version: '1.0.0',
            request: 'GetFeature',
            typeName: LayerManager.findConfig('Admin_BMC')?.id ?? '',
            outputFormat: 'application/json',
            propertyName: 'BMC',
        }).toString()}`,
    }),
    (root) => {
        if (root.type !== 'FeatureCollection') return [];
        return root.features.map((feature: any) => feature.properties?.BMC);
    },
);

export const exportData = makeAPICall<
    ArrayBuffer,
    {
        layerConfig: LayerManager.LayerConfig;
        cqlFilter?: string;
        propertyName?: string;
        outputFormat: string;
        maxFeature: number;
    },
    ArrayBuffer
>(
    ({ layerConfig, cqlFilter, propertyName, outputFormat, maxFeature }) => {
        const query = {
            service: 'WFS',
            version: '1.1.0',
            request: 'GetFeature',
            typeName: layerConfig?.serviceName ?? '',
            srs: DATA_PROJECTION.getCode(),
            outputFormat: outputFormat,
            maxFeatures: maxFeature,
            ...(cqlFilter && { cql_filter: cqlFilter }),
            ...(propertyName && { propertyName: propertyName }),
        };
        return {
            url: `${GEOSERVER_URL}/ows?${convertParameters(query)}`,
            endpoint: GEOSERVER_URL,
            skipAuth: false,
            dataType: 'blob',
        };
    },
    (data) => {
        return data;
    },
);
